import { createContext, useContext, useState } from "react"

export const ResearchContext = createContext(null)

function shuffleArray(array) {
    const result = [...array]; // copy to avoid mutating original
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

export const ResearchProvider = ({ children }) => {

    const [isFetched, setIsFetched] = useState(false)
    const [advertisersToExternalAdsMaps, setAdvertisersToExternalAdsMaps] = useState({})
    const [boardToExternalAdsMaps, setBoardToExternalAdsMaps] = useState({})
    const [advertisers, setAdvertisers] = useState({})
    const [advertisersCreativeStrategies, setAdvertisersCreativeStrategies] = useState({})
    const [externalAds, setExternalAds] = useState({})
    const [followedAdvertisers, setFollowedAdvertisers] = useState([])
    const [boards, setBoards] = useState([])
    const [isAnalyzeAdsPerBrand, setIsAnalyzeAdsPerBrand] = useState({})

    /* Advertisers */
    const getAllAdvertisersFromState = () => Object.values(advertisers)
    const getAdvertiserFromState = (advertiserId) => advertisers[advertiserId]
    const updateManyAdvertiserState = (advertisersToUpdate, override = false) => {
        const newState = override ? {} : JSON.parse(JSON.stringify(advertisers))

        for (const advertiser of advertisersToUpdate) {
            newState[advertiser._id] = advertiser
        }
        setAdvertisers(newState)
    }
    const updateAdvertiserStateById = (advertiserId, newData) => {
        const newState = JSON.parse(JSON.stringify(advertisers))
        newState[advertiserId] = newData
        setAdvertisers(newState)
    }
    const removeAdvertiserFromState = (advertiserId) => {
        const newState = JSON.parse(JSON.stringify(advertisers))
        delete newState[advertiserId]
        setAdvertisers(newState)
    }
    const getAdvertiserAdsIdsFromState = (advertiserId) => advertisers[advertiserId]
    /* Followed Advertisers - not in use currently, will do it once we will need a 'discovery' feature */
    const getAllFollowedAdvertisersFromState = () => followedAdvertisers

    const updateFollowedAdvertisersState = (advertisersToUpdate, override = false) => {
        const newState = override ? [] : JSON.parse(JSON.stringify(followedAdvertisers))
        for (const advertiserId of advertisersToUpdate) {
            newState.push(advertiserId)
        }
        setFollowedAdvertisers([...new Set(newState)]) // prevent duplications
    }

    const removeFollowedAdvertiserFromState = (advertiserId) => {
        const newState = JSON.parse(JSON.stringify(followedAdvertisers))
        newState.splice(newState.indexOf(advertiserId), 1)
        setFollowedAdvertisers(newState)
    }
    const isFollowingAdvertiser = (advertiserId) => followedAdvertisers.includes(advertiserId)

    /* Advertisers to Ads relation */
    const getAdvertisersExternalAdsFromState = (advertiserId) => advertisersToExternalAdsMaps[advertiserId]
    const updateAdvertisersToExternalAdsMapsState = (advertiserId, externalAdsIds) => {
        const newState = JSON.parse(JSON.stringify(advertisersToExternalAdsMaps))
        newState[advertiserId] = externalAdsIds
        setAdvertisersToExternalAdsMaps(newState)
    }
    const updateManyAdvertisersToExternalAdsMapsState = (advertisersToExternalAdsMapsToUpdate) => {
        const newState = JSON.parse(JSON.stringify(advertisersToExternalAdsMaps))
        setAdvertisersToExternalAdsMaps({ ...newState, ...advertisersToExternalAdsMapsToUpdate })
    }


    /* Ads */
    const getRandomExternalAdsFromState = () => Object.values(externalAds).sort(() => 0.5 - Math.random()).slice(0, 50)
    const getExternalAdFromState = (externalAdId) => externalAds[externalAdId]
    const updateManyExternalAdsState = (externalAdsToUpdate, override = false) => {
        externalAdsToUpdate = shuffleArray(externalAdsToUpdate)
        const newState = override ? {} : JSON.parse(JSON.stringify(externalAds))
        for (const externalAd of externalAdsToUpdate) {
            newState[externalAd._id] = externalAd
        }
        setExternalAds(newState)
    }
    const updateExternalAdStateById = (externalAdId, newData) => {
        const newState = JSON.parse(JSON.stringify(externalAds))
        newState[externalAdId] = newData
        setExternalAds(newState)
    }
    const removeExternalAdsFromState = (externalAdIds) => {
        const newState = JSON.parse(JSON.stringify(externalAds))
        for (const externalAdId of externalAdIds) {
            delete newState[externalAdId]
        }
        setExternalAds(newState)
    }

    /* Boards */
    const getAllBoardsFromState = () => boards
    const getBoardFromState = (boardId) => boards.find(board => board._id === boardId)
    const updateManyBoardsState = (boardsToUpdate, override = false) => {
        const newState = override ? [] : JSON.parse(JSON.stringify(boards))
        for (const board of boardsToUpdate) {
            newState.push(board)
        }
        setBoards(newState)
    }
    const updateBoardStateById = (boardId, newData) => {
        const newState = JSON.parse(JSON.stringify(boards))
        const index = newState.findIndex(board => board._id === boardId)
        if (index !== -1) {
            newState[index] = newData
        }
        setBoards(newState)
    }
    const removeBoardsFromState = (boardId) => {
        const newState = [...boards]
        newState.splice(newState.findIndex(board => board._id === boardId), 1)
        setBoards(newState)
    }
    const getBoardToExternalAdsMapsFromState = (boardId) => boardToExternalAdsMaps[boardId]
    const updateBoardToExternalAdsMapsState = (boardId, externalAdsIds) => {
        const newState = JSON.parse(JSON.stringify(boardToExternalAdsMaps))
        newState[boardId] = externalAdsIds
        setBoardToExternalAdsMaps(newState)
    }
    const updateManyBoardToExternalAdsMapState = (boardsWithTheirAds) => {
        const newState = JSON.parse(JSON.stringify(boardToExternalAdsMaps))
        for (const board of boardsWithTheirAds) {
            newState[board._id] = board.externalAds
        }
        setBoardToExternalAdsMaps(newState)
    }

    /* Advertisers Creative Strategies */
    const getCreativeStrategyOfAdvertiserFromState = (advertiserId) => advertisersCreativeStrategies[advertiserId]
    const updateAdvertiserCreativeStrategiesState = (advertiserId, newData) => {
        const newState = JSON.parse(JSON.stringify(advertisersCreativeStrategies))
        newState[advertiserId] = newData
        setAdvertisersCreativeStrategies(newState)
    }


    return (
        <ResearchContext.Provider
            value={{
                // External Ads
                removeExternalAdsFromState,
                getRandomExternalAdsFromState,
                getExternalAdFromState,
                updateManyExternalAdsState,
                updateExternalAdStateById,
                // Advertisers
                removeAdvertiserFromState,
                getAllAdvertisersFromState,
                getAdvertiserFromState,
                updateManyAdvertiserState,
                updateAdvertiserStateById,
                getAdvertisersExternalAdsFromState,
                updateAdvertisersToExternalAdsMapsState,
                updateManyAdvertisersToExternalAdsMapsState,
                getAdvertiserAdsIdsFromState,
                // Followed Advertisers
                getAllFollowedAdvertisersFromState,
                updateFollowedAdvertisersState,
                removeFollowedAdvertiserFromState,
                isFollowingAdvertiser,
                // Boards
                getBoardToExternalAdsMapsFromState,
                updateBoardToExternalAdsMapsState,
                updateManyBoardToExternalAdsMapState,
                updateManyBoardsState,
                updateBoardStateById,
                removeBoardsFromState,
                getAllBoardsFromState,
                getBoardFromState,

                // Creative Strategies
                getCreativeStrategyOfAdvertiserFromState,
                updateAdvertiserCreativeStrategiesState,

                // Generic
                setIsFetched,
                isFetched,
                isAnalyzeAdsPerBrand,
                setIsAnalyzeAdsPerBrand
            }}
        >
            {children}
        </ResearchContext.Provider>
    )
}

export const useResearchContext = () => {
    const context = useContext(ResearchContext)

    if (!context) {
        throw new Error('useResearchContext must be used within a ResearchProvider')
    }

    return context
}